import { useApolloClient, useSubscription } from '@apollo/client';
import type { Socket } from 'phoenix';
import React, { lazy, Suspense, useEffect, useMemo, useRef } from 'react';
import { ScrollRestoration, useLocation, useMatches } from 'react-router-dom';
import localLogout, {
  shouldIgnoreLocalLogout,
} from '../../account/localLogout';
import { IS_CAPACITOR } from '../../constants/platform';
import VersionMismatchModal from '../../error/VersionMismatchModal';
import useAutoShowInvitationModalEffect from '../../organization/useAutoShowInvitationModalEffect';
import UIActions from '../../ui/UIActions';
import { MainContentHistoryProvider } from '../../utils/router/useMainContentHistory';
import useNavigate from '../../utils/router/useNavigate';
import useSlug from '../../utils/router/useSlug';
import { createPostPath } from '../../utils/url/postPath';
import useShortcut from '../../utils/useShortcut';
import useAppSelector, { useAppDispatch } from '../redux/useAppSelector';
import EnsureAdmin from '../router/EnsureAdmin';
import EnsureLoggedIn from '../router/EnsureLoggedIn';
import EnsureLoggedOut from '../router/EnsureLoggedOut';
import type { PageLayoutElements } from './PageLayout';
import PageLayout from './PageLayout';
import { gql } from '../../graphql/generated/gql';
import useScript from '../../utils/network/useScript';

interface Match {
  handle: {
    routeName?: string;
    pageLayout?: PageLayoutElements;
    ensureRole?: 'user' | 'admin' | 'anonymous';
  };
}

const { __slab } = window;

const CURRENT_VERSION_QUERY = gql(`
  query currentVersion {
    currentVersion {
      version
    }
  }
`);

const SESSION_LOGOUT_SUBSCRIPTION = gql(`
  subscription sessionLogout {
    sessionLogout
  }
`);

const ROLE_WRAPPER = {
  user: EnsureLoggedIn,
  admin: EnsureAdmin,
  anonymous: EnsureLoggedOut,
};

const AppRootCapacitorHelpers = IS_CAPACITOR
  ? lazy(() => import('../../capacitor/AppRootCapacitorHelpers'))
  : () => null;

const useGoogleAnalytics = () => {
  // We delay the initialization of Google Analytics instead of using a script tag
  // to make sure page title is set correctly before the page view event is sent.
  const id = window.__slab.GOOGLE_ANALYTICS_MEASUREMENT_ID;

  const hasInitializedRef = useRef(false);
  if (!hasInitializedRef.current) {
    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag() {
      // Google Analytics doesn't work with rest parameters
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer?.push(arguments);
    };

    window.gtag('js', new Date());
    window.gtag('config', id);

    hasInitializedRef.current = true;
  }

  useScript(`https://www.googletagmanager.com/gtag/js?id=${id}`);
};

const AppRoot = () => {
  const [topicId] = useSlug('topicSlug');
  const navigate = useNavigate();
  useShortcut('shift+n', (e) => {
    e.preventDefault();
    navigate(createPostPath({ topicId: topicId || undefined }));
  });

  useGoogleAnalytics();
  const dispatch = useAppDispatch();
  const isLoggedIn = useAppSelector((state) => !!state.session.user.id);
  const socket = useAppSelector((state) => state.session.socket);

  const client = useApolloClient();

  const checkVersion = useMemo(
    () => () => {
      client
        .query({
          query: CURRENT_VERSION_QUERY,
          fetchPolicy: 'network-only',
        })
        .then(
          ({
            data: {
              currentVersion: { version },
            },
          }) => {
            const [serverMajor, serverMinor] = version.split('.');
            const [clientMajor, clientMinor] = __slab.VERSION.split('.');

            // Tab bar page shouldn't display version mismatch modal.
            if (
              !IS_CAPACITOR &&
              window.location.pathname !== '/desktop/tabbar' &&
              (serverMinor !== clientMinor || serverMajor !== clientMajor)
            ) {
              dispatch(
                UIActions.openModal(VersionMismatchModal, {
                  close: () => {}, // Prevent user from closing
                }),
              );
            }
          },
        )
        .catch(() => {});
    },
    [client, dispatch],
  );

  useSubscription(SESSION_LOGOUT_SUBSCRIPTION, {
    skip: !isLoggedIn,
    onData() {
      if (!shouldIgnoreLocalLogout()) {
        localLogout();
      }
    },
  });

  const prevSocketRef = useRef<Socket>();

  useEffect(() => {
    if (socket) {
      socket.onOpen(checkVersion);
    }

    if (prevSocketRef.current == null && socket != null) {
      checkVersion();
    }

    prevSocketRef.current = socket;
  }, [checkVersion, socket]);

  useAutoShowInvitationModalEffect();

  const location = useLocation();
  const matches = useMatches();
  const {
    routeName = '',
    pageLayout = {},
    ensureRole,
  } = matches.reduce(
    (acc: Match['handle'], match) => ({
      ...acc,
      ...(match as Match).handle,
    }),
    {},
  );

  useEffect(() => {
    if (routeName) {
      analytics.page(routeName);
      window.Intercom?.('update');
    }
  }, [routeName, location]);

  const EnsureWrapper =
    (ensureRole && ROLE_WRAPPER[ensureRole]) || React.Fragment;

  return (
    <MainContentHistoryProvider
      skipLocation={!pageLayout.sidebar || !pageLayout.topbar}
    >
      <ScrollRestoration />
      <Suspense fallback={null}>
        <AppRootCapacitorHelpers />
      </Suspense>
      <EnsureWrapper>
        <PageLayout elements={pageLayout} routeName={routeName} />
      </EnsureWrapper>
    </MainContentHistoryProvider>
  );
};

export default () => <AppRoot />;
